import type { Config as DOMPurifyConfig } from 'dompurify';

/**
 * Shared configuration for the rich-text render path (issue #997).
 *
 * `DOMPURIFY_CONFIG` allowlists the tags/attributes `RichTextViewer` sanitizes
 * `quill.getSemanticHTML()` output against before inserting it into the DOM —
 * see documents/specs/rich-text-spec.md § "DOMPurify Configuration". The
 * allowlist mirrors `RichTextEditor`'s `ALLOWED_FORMATS` (the Quill formats a
 * user can actually produce): headings, inline emphasis, lists, code, links,
 * and Quill's `<span>`-based color/background/size formatting.
 */
export const DOMPURIFY_CONFIG: DOMPurifyConfig = {
  ALLOWED_TAGS: [
    'p',
    'br',
    'strong',
    'em',
    'u',
    's',
    'code',
    'pre',
    'h1',
    'h2',
    'h3',
    'blockquote',
    'ol',
    'ul',
    'li',
    'a',
    'span',
  ],
  ALLOWED_ATTR: ['href', 'class', 'style'],
  ALLOW_DATA_ATTR: false,
  FORCE_BODY: true,
};

/** Link protocols permitted in the `link` Quill format (spec § "Link Validation"). */
const ALLOWED_URL_PROTOCOLS: ReadonlySet<string> = new Set(['https:', 'mailto:']);

/**
 * Validates a URL before it is applied via `quill.format('link', url)` or
 * rendered as an `<a href>` by `RichTextViewer`. Only `https:` and `mailto:`
 * are permitted — this rejects `javascript:`, `data:`, `vbscript:`, bare
 * `http:`, and relative paths (which fail `new URL()` without a base and are
 * therefore treated as disallowed rather than resolved against the page origin).
 */
export function isAllowedUrl(url: string): boolean {
  try {
    const parsed = new URL(url);
    return ALLOWED_URL_PROTOCOLS.has(parsed.protocol);
  } catch {
    return false;
  }
}
